import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IndicatorDoc } from '@core/sim/indicators/dsl/schema';
import { sourceFeatureLabels } from '@core/sim/indicators/dsl/marketFeatures';
import { fetchIndicatorGallery, type IndicatorGalleryItem } from '../../data-access/indicatorGallerySupabase';
import { renderCustomIndicators } from './useCustomIndicatorRender';
import type { Candle } from '../sim/PriceChart';
import { CSBadge, CSCard, CSIcon } from '../_components/cs';

export const INDICATOR_SAMPLE_CANDLES: Candle[] = Array.from({ length: 24 }, (_, i) => {
  const close = 100 + i * 0.45 + Math.sin(i / 2) * 2.2;
  return { t: (1704067200 + i * 86400) as Candle['t'], o: close - 0.4, h: close + 1.2, l: close - 1.1, c: close, v: 1000 + i * 17 };
});

export function IndicatorSparkline({ id, name, pane, doc, height = 54 }: { id: string; name: string; pane: 'main' | 'sub'; doc: IndicatorDoc; height?: number }) {
  const renders = renderCustomIndicators(INDICATOR_SAMPLE_CANDLES, { version: 1, items: [{ id, name, source: 'community', pane, doc, enabled: true, createdAt: 0, updatedAt: 0 }] });
  const values = INDICATOR_SAMPLE_CANDLES.map((c) => c.c);
  const min = Math.min(...values), max = Math.max(...values), span = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * 100},${38 - ((v - min) / span) * 32}`).join(' ');
  return <svg aria-label={`${name} 지표 미리보기`} data-render-series={renders.length} viewBox="0 0 100 42" width="100%" height={height} style={{ display: 'block' }}>
    <polyline points={points} fill="none" stroke="var(--cs-accent)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
  </svg>;
}

export function IndicatorAuthor({ item }: { item: IndicatorGalleryItem }) {
  const initial = item.author_nick.trim().slice(0, 1) || '지';
  return <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
    <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: 'var(--cs-r-pill)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: 'var(--cs-surface-2)', color: 'var(--cs-accent)', fontSize: 11, fontWeight: 800 }}>{initial}</span>
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--cs-ink-2)', fontSize: 11.5, fontWeight: 700 }}>{item.author_nick}</span>
    {item.author_title && <span style={{ flexShrink: 0, color: 'var(--cs-mute)', fontSize: 10.5 }}>{item.author_title}</span>}
  </div>;
}

export function IndicatorComposition({ item }: { item: IndicatorGalleryItem }) {
  const stats = item.stats ?? { inputs: 0, nodes: 0, plots: 0, maxLookback: 0 };
  const sourceLabels = sourceFeatureLabels(item.features ?? []);
  return <div>
    <div style={{ color: 'var(--cs-mute)', fontSize: 10.5, lineHeight: 1.5 }}>재료 {stats.inputs} · 계산 {stats.nodes}줄 · 그리기 {stats.plots}개 · 준비 기간 {stats.maxLookback}일</div>
    {sourceLabels.length > 0 && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>{sourceLabels.map((label) => <CSBadge key={label} tone="neutral">{label}</CSBadge>)}</div>}
  </div>;
}

export function IndicatorGalleryCard({ item, onClick, onBrowse, variant = 'gallery' }: { item: IndicatorGalleryItem; onClick?: () => void; onBrowse?: () => void; variant?: 'gallery' | 'home' }) {
  if (variant === 'home') return <CSCard padded onClick={onClick} style={{ cursor: 'pointer', minWidth: 0, overflow: 'hidden', marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--cs-mute)', fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em' }}>
      <span>이번 주 인기 지표</span>
      <span role="link" aria-label="지표 갤러리로 이동" tabIndex={0} onClick={(event) => { event.stopPropagation(); onBrowse?.(); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onBrowse?.(); } }} style={{ display: 'inline-flex', color: 'var(--cs-mute)', cursor: 'pointer' }}>
        <CSIcon name="arrow" size={14} />
      </span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 92px', alignItems: 'center', gap: 12, marginTop: 8 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, lineHeight: 1.35, fontWeight: 800, color: 'var(--cs-ink)', overflowWrap: 'anywhere' }}>{item.name}</div>
        <div style={{ marginTop: 4, color: 'var(--cs-mute)', fontSize: 11 }}>{item.author_nick}</div>
      </div>
      <IndicatorSparkline id={item.version_id} name={item.name} pane={item.pane} doc={item.doc} height={48} />
    </div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 8, paddingTop: 10, boxShadow: 'inset 0 1px 0 var(--cs-line)' }}>
      <IndicatorComposition item={item} />
      <span style={{ marginLeft: 'auto', flexShrink: 0, color: 'var(--cs-accent)', fontSize: 11.5, fontWeight: 800 }}>구경하기</span>
    </div>
  </CSCard>;

  return <CSCard padded onClick={onClick} style={{ cursor: 'pointer', minWidth: 0, overflow: 'hidden' }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 92px', alignItems: 'center', gap: 12 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, lineHeight: 1.35, fontWeight: 800, color: 'var(--cs-ink)', overflowWrap: 'anywhere' }}>{item.name}</div>
        <div style={{ marginTop: 8 }}><IndicatorAuthor item={item} /></div>
      </div>
      <IndicatorSparkline id={item.version_id} name={item.name} pane={item.pane} doc={item.doc} height={48} />
    </div>
    <div style={{ marginTop: 10 }}><IndicatorComposition item={item} /></div>
  </CSCard>;
}

export function IndicatorGalleryList() {
  const navigate = useNavigate();
  const [items, setItems] = useState<IndicatorGalleryItem[]>([]);
  const [error, setError] = useState('');
  useEffect(() => { fetchIndicatorGallery().then(setItems).catch(() => setError('지표를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.')); }, []);
  return <div style={{ padding: '0 16px 24px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10 }}>
    {error ? <div role="alert" style={{ padding: 24, color: 'var(--cs-mute)', fontSize: 13 }}>{error}</div>
      : items.length ? items.map((item) => <IndicatorGalleryCard key={item.version_id} item={item} onClick={() => navigate(`/community/indicator/${item.version_id}`)} />)
        : <div style={{ padding: 32, textAlign: 'center', color: 'var(--cs-mute)', fontSize: 13 }}>공개된 지표가 아직 없어요.</div>}
    <button type="button" onClick={() => navigate('/indicator-studio')}
      style={{ minHeight: 44, marginTop: 6, border: 0, background: 'transparent', color: 'var(--cs-accent)', fontSize: 13, fontWeight: 800 }}>나도 만들기 →</button>
  </div>;
}
